import React from "react";
import clsx from "clsx";
import { shallowEqual } from "react-redux";
import styles from "./Cta.module.scss";
import { setFinanceOption } from "../../../../../../../common-deprecated/redux/actions/UtilActions";
import Toggle from "../../../../../shared-components/css-modules/toyota/atoms/Toggle";
import Button from "../../../../../shared-components/css-modules/toyota/atoms/Button";
import Icon from "../../../../../shared-components/css-modules/toyota/atoms/Icon";
import { useCarConfigSelector } from "../../../../redux/store";
import { MODAL_FINANCE_CALCULATOR } from "../../../../utils/modalConstants";
import { financeCtaSelector, pricingDetailsSelector } from "../../../../utils/selectorUtils";
import Price from "../price/Price";
import useAphModal from "../../../../hooks/useAphModal";
import Text from "../../../../../shared-components/css-modules/toyota/atoms/Text";
import { getFinanceQuoteIdFromStorage, getSessionStorageParams } from "../../../../utils/sessionStorage";
import a11y from "../../../../../shared-components/css-modules/toyota/utils/Accessibility.module.scss";
import { CarConfigLabel, useCarConfigLabel } from "../../../../utils/labelUtils";
import { trackPriceDisplayChange } from "../../../../utils/tracking";
import { useDispatch } from "../../../../../../../shared-logic/redux/store";

const FinanceWrapper: React.FC = () => {
    const dispatch = useDispatch();
    const basketTotal = useCarConfigSelector((state) => state.basket.total);
    const financeOption = useCarConfigSelector((state) => state.utils.financeOption);
    const section = useCarConfigSelector((state) => state.carConfigNavigation.activeSection);
    const { id: modelId } = useCarConfigSelector((state) => state.car.model);
    const sessionStorageParams = useCarConfigSelector(getSessionStorageParams);
    const { showFinance, priceReadyToShow } = useCarConfigSelector(financeCtaSelector, shallowEqual);
    const { forceCashPrice, hideFinanceDetailsHeader } = useCarConfigSelector(pricingDetailsSelector, shallowEqual);

    const isCashSelected = financeOption === "cash";

    const [
        carConfigCashToggleLabel,
        carConfigFinanceToggleLabel,
        carConfigSelectedPaymentLabel,
        carConfigSwitchPaymentLabel,
        carConfigAriaCashToggleLabel,
        carConfigAriaFinanceToggleLabel,
    ] = useCarConfigLabel([
        "carConfigCashToggle",
        "carConfigFinanceToggle",
        "carConfigPaymentMethod",
        isCashSelected ? "carConfigSwitchToFinance" : "carConfigSwitchToCash",
        "carConfigA11yButtonSwitchToCash",
        "carConfigA11yButtonSwitchToFinance",
    ]);

    const { showModalHelper } = useAphModal();

    const toggleFinance = (openCalculator?: boolean): void => {
        if (openCalculator || isCashSelected) {
            // Check if financeQuote has been filled in
            const financeQuoteId = getFinanceQuoteIdFromStorage(sessionStorageParams, modelId);

            // If so. Just switch to monthly
            if (!openCalculator && financeQuoteId) {
                dispatch(setFinanceOption("monthly"));
                trackPriceDisplayChange(section, "monthly");
            } else {
                // If not. Open Calculator
                showModalHelper(MODAL_FINANCE_CALCULATOR, {
                    step: section,
                    origin: "cc-navigation-top",
                });
                trackPriceDisplayChange(section, "monthly");
            }
        } else {
            dispatch(setFinanceOption("cash"));
            trackPriceDisplayChange(section, "cash");
        }
    };

    return (
        <div className={styles["finance-wrapper"]}>
            <div
                className={clsx(
                    styles.finance,
                    !showFinance && styles["finance-disabled"],
                    !isCashSelected && styles["finance-selected"],
                )}
            >
                <div className={styles.pricing}>
                    <div>
                        {showFinance && (
                            <Text className={styles["mobile-payment-label"]} variant="apheleia-body">
                                {carConfigSelectedPaymentLabel}
                            </Text>
                        )}
                        {!(!isCashSelected && hideFinanceDetailsHeader) && (
                            <Price
                                price={basketTotal}
                                forceCashPrice={forceCashPrice}
                                readyToShow={priceReadyToShow}
                                financeItemId="total"
                            />
                        )}
                    </div>
                    {!isCashSelected && (
                        <Button
                            onClick={() => toggleFinance(true)}
                            className={styles["edit-finance-icon"]}
                            variant="clean"
                        >
                            <Icon className={styles.icon} sizing="sm" variant="pencil" aria-hidden="true" />
                            <span className={a11y["sr-only"]}>
                                <CarConfigLabel label="carConfigA11yButtonEdit" />
                            </span>
                        </Button>
                    )}
                </div>

                {/* Finance button or toggle */}
                {showFinance && (
                    <>
                        <Toggle
                            className={styles["finance-toggle-desktop"]}
                            onClick={() => toggleFinance()}
                            firstOptionSelected={isCashSelected}
                            firstOption={carConfigCashToggleLabel}
                            secondOption={carConfigFinanceToggleLabel}
                        />
                        <Button
                            onClick={() => toggleFinance()}
                            variant="clean"
                            className={styles["finance-toggle-mobile"]}
                            aria-label={isCashSelected ? carConfigAriaFinanceToggleLabel : carConfigAriaCashToggleLabel}
                        >
                            <Text variant="apheleia-body">{carConfigSwitchPaymentLabel}</Text>
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};

export default FinanceWrapper;
